/* eslint-disable promise/always-return */
import { useEffect, useState } from 'react';
import braintree from 'braintree-web';
import {
  useStripe,
  useElements,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  Typography,
  Radio,
  Stack,
  Alert,
  CircularProgress,
  FormControlLabel,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
} from '@mui/material';
import {
  selectIsLoggedIn,
  selectUserRole,
  selectUser,
} from 'renderer/features/auth/state/userSelectors';
import { useGetCartQuery } from 'renderer/features/payments-page/services/cart.endpoints';
import { useListPaymentMethodsQuery } from 'renderer/features/settings/services/paymentMethods.endpoints';
import { useAppSelector } from 'renderer/shared/state/hooks';
import AccountForm from 'renderer/features/settings/components/AccountForm';
import BillingAddressForm from 'renderer/features/settings/components/BillingAddressForm';
import { useGetProductDetailsQuery } from 'renderer/features/products-page/services/products.endpoints';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { Form, Formik } from 'formik';
import { ZodSchema } from 'zod';
import { toValidator } from 'renderer/shared/schemas/common.schemas';
import OrderSummary from './OrderSummary';
import DiscountCode from './DiscountCode';
import MainCard from './MainCard';

import {
  LoggedInUserExistingCardSchema,
  LoggedInUserNewCardSchema,
  PublicUserNewAccountSchema,
  PublicUserNoAccountSchema,
  PurchaseForm,
} from '../schemas/purchase.schemas';
import {
  useGetConfigQuery,
  useMakePurchaseMutation,
} from '../services/purchase.endpoints';
import PurchaseConfirmationPage from './PurchaseConfirmationPage';
import StripeCreditCardForm from './StripeCreditCardForm';
import ExistingPaymentMethods from './ExistingPaymentMethods';
import PaymentMethodForm from './PaymentMethodForm';
// import "./styles.css";

const FullScreenLoader = ({ message }: { message: string }) => {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        zIndex: 1000,
      }}
    >
      <CircularProgress />
      {message && (
        <Typography variant="h6" sx={{ mt: 2 }}>
          {message}
        </Typography>
      )}
    </Box>
  );
};

const initialForm: PurchaseForm = {
  nameOnCard: '',
  email: '',
  firstName: '',
  lastName: '',
  streetAddress: '',
  locality: '',
  region: '',
  regionCode: '',
  postalCode: '',
  countryName: '',
  countryCode: '',
  password: '',
  confirmPassword: '',
  createAccount: false,
  savePayment: false,
  makeDefault: false,
  discountCode: '',
  // hostApplication: {
  //   id: '',
  //   version: '',
  //   displayName: '',
  // },
  // platform: '',
  isNewPaymentMethod: true,
  newPaymentMethodToken: '',
  numberValidity: '',
  cvvValidity: '',
  expirationDateValidity: '',
  existingPaymentMethodToken: '',
  // numberOfCopies: 1,
  selectedPlan: '',
} as const;

function Purchase() {
  const role = useAppSelector(selectUserRole);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Stripe hooks
  const stripe = useStripe();
  const elements = useElements();

  const { id: productId } = useParams();
  const { data: productDetails, error: productError } =
    useGetProductDetailsQuery(productId ?? '');
  console.log('productDetails', productDetails);
  const { data: config, error: configError } = useGetConfigQuery();
  const [
    makePurchase,
    {
      data: confirmation,
      isSuccess: purchaseSuccess,
      error: purchaseError,
      isLoading: purchaseLoading,
    },
  ] = useMakePurchaseMutation();
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const [hostedFieldsInstance, setHostedFieldsInstance] =
    useState<braintree.HostedFields | null>(null);
  const user = useAppSelector(selectUser);

  // const { data: cartData } = useGetCartQuery();
  const { data: cartData, refetch: refetchCart } = useGetCartQuery(null);
  console.log('cartData', cartData);

  const provider = cartData?.items?.some(
    (item: any) => item.productId?.category === 'Subscription',
  )
    ? 'stripe'
    : 'braintree';
  console.log('provider', provider);

  console.log('user', user);

  const category = provider === 'stripe' ? 'Subscription' : 'Video Plugin';
  const { data: paymentMethods } = useListPaymentMethodsQuery(category);

  const { data: stripePaymentMethods } = useListPaymentMethodsQuery('stripe');
  const { data: braintreePaymentMethods } =
    useListPaymentMethodsQuery('braintree');
  // 🔁 Redirect logic - TEMPORARY FIX FOR CUSTOMERS GOING TO OLD FF2 LINK
  const restrictedProductId = '66341fef4895dadba053975d'; // FF2
  const newProductId = '67f6b5c241f8c9345e025d6d'; // FF3
  // useEffect(() => {
  //   if (
  //     (role === 'user' || role === 'public') &&
  //     productId === restrictedProductId
  //   ) {
  //     navigate(`/purchase/${newProductId}`, { replace: true });
  //   }
  // }, [role, productId, navigate]);

  // If the user is not logged in and making a public purchase for a subscription,
  // create an account and save the payment method
  // useEffect(() => {
  //   if (productDetails?.category === 'Subscription' && !isLoggedIn) {
  //     // setFormData((prev) => ({
  //     //   ...prev,
  //     //   createAccount: true,
  //     //   savePayment: true,
  //     // }));
  //   }
  // }, [productDetails, isLoggedIn]);

  // if (productError) {
  //   return <FullScreenLoader message={formatQueryError(productError)} />;
  // }
  if (configError) {
    return <FullScreenLoader message={formatQueryError(configError)} />;
  }
  // if (!productDetails || !config) {
  //   return <FullScreenLoader message="Loading..." />;
  // }

  const braintreeClientToken = config?.braintreeClientToken;

  if (purchaseSuccess && confirmation) {
    return <PurchaseConfirmationPage {...confirmation} />;
  }

  const handleSubmit = async (
    values: typeof initialForm,
    actions: { setFieldError: (arg0: string, arg1: string) => void },
  ) => {
    console.log('handleSubmit');
    console.log('productDetails', productDetails);
    if (isSubmitting) return;

    setIsSubmitting(true);

    let newPaymentMethodToken = '';
    // const provider =
    //   productDetails?.category === 'Subscription' ? 'stripe' : 'braintree';

    const cardProvider = cartData?.items?.some(
      (item: any) => item.productId?.category === 'Subscription',
    )
      ? 'stripe'
      : 'braintree';

    // if (productDetails?.category === 'Subscription') {
    if (cardProvider === 'stripe') {
      if (!stripe || !elements) {
        setIsSubmitting(false);
        return;
      }

      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        setIsSubmitting(false);
        return;
      }

      const payload = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });
      newPaymentMethodToken = payload.paymentMethod?.id || '';
    } else if (hostedFieldsInstance && values.isNewPaymentMethod) {
      try {
        const { nonce } = await hostedFieldsInstance.tokenize();
        newPaymentMethodToken = nonce;
      } catch (error) {
        // Handle the error here
        actions.setFieldError(
          'newPaymentMethodToken',
          'Encountered an error while tokenizing card details. Please try again.',
        );
      }
    }

    console.log('newPaymentMethodToken', newPaymentMethodToken);

    const {
      createAccount,
      firstName,
      lastName,
      email,
      password,
      confirmPassword,
      savePayment,
      existingPaymentMethodToken,
      isNewPaymentMethod,
      nameOnCard,
      streetAddress,
      locality,
      region,
      postalCode,
      countryName,
      discountCode,
      // hostApplication,
      // platform,
      // numberOfCopies,
      makeDefault,
    } = values;

    const request = {
      user: {
        id: '_id' in user ? user._id : undefined,
        create: createAccount,
        firstName,
        lastName,
        email,
        password,
        confirmPassword,
      },
      paymentMethod: {
        provider: cardProvider,
        id: isNewPaymentMethod ? undefined : existingPaymentMethodToken,
        create: isNewPaymentMethod ? savePayment : undefined,
        makeDefault: isNewPaymentMethod ? makeDefault : undefined,
        token: isNewPaymentMethod ? newPaymentMethodToken : undefined,
        billingAddress: isNewPaymentMethod
          ? {
              nameOnCard,
              streetAddress,
              locality,
              region,
              postalCode,
              countryName,
            }
          : undefined,
      },
      // purchase: {
      //   productId: productDetails._id,
      //   priceId: productDetails.priceId,
      //   discountCode,
      //   serial,
      //   hostApplication,
      //   platform,
      //   numberOfCopies,
      // },
      cartId: cartData?._id,
      purchases: cartData?.items.map((item: any, index: number) => ({
        productId: item.productId._id || item.productId,
        numberOfCopies: item.quantity,
        platform: item.platform,
        hostApplication: item.hostApplication,
        serial: item.serial,
        isFinal: index === cartData.items.length - 1,
      })),
    };
    console.log('request', request);
    console.log(JSON.stringify(request));
    makePurchase(request)
      .unwrap()
      .then(() => {
        refetchCart();
      })
      .catch((err) => {
        console.error('Purchase failed', err);
      });
    setIsSubmitting(false);
  };

  // const provider = cartData?.items?.some(
  //   (item: any) => item.productId?.category === 'Subscription',
  // )
  //   ? 'stripe'
  //   : 'braintree';
  // console.log('provider', provider);

  // console.log('user', user);

  const hasSavedCards =
    isLoggedIn && paymentMethods && paymentMethods?.length > 0;
  const defaultCard = hasSavedCards
    ? paymentMethods.find((pm) => pm.default) || paymentMethods[0]
    : null;

  if (isLoggedIn && !paymentMethods) {
    return <FullScreenLoader message="Loading payment methods..." />;
  }

  const shouldShowStripeAlert =
    category === 'Video Plugin' &&
    paymentMethods?.length === 0 &&
    (stripePaymentMethods?.length ?? 0) > 0;

  const shouldShowBraintreeAlert =
    category === 'Subscription' &&
    paymentMethods?.length === 0 &&
    (braintreePaymentMethods?.length ?? 0) > 0;

  return (
    <Formik
      initialValues={{
        ...initialForm,
        isNewPaymentMethod: !hasSavedCards,
        existingPaymentMethodToken: defaultCard?._id ?? '',
      }}
      validate={(values) => {
        let validationSchema: ZodSchema = PublicUserNoAccountSchema;
        if (values.createAccount) validationSchema = PublicUserNewAccountSchema;
        if (isLoggedIn) validationSchema = LoggedInUserNewCardSchema;
        if (!values.isNewPaymentMethod)
          validationSchema = LoggedInUserExistingCardSchema;
        return toValidator(validationSchema)(values);
      }}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => {
        if (purchaseLoading) {
          return <FullScreenLoader message="Processing your purchase..." />;
        }

        return (
          <Form>
            <Box
              component="main"
              sx={{
                flexGrow: 1,
                pt: 0,
                pr: { xs: 2, sm: 5 },
                pb: { xs: 2, sm: 5 },
                pl: { xs: 2, sm: 5 },
              }}
            >
              {/* <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                <MuiLink underline="hover" color="inherit" href="/">
                  Home
                </MuiLink>
                <Typography color="text.primary" fontWeight="bold">
                  Purchase
                </Typography>
              </Breadcrumbs> */}
              {/* <Typography variant="h2" sx={{ mb: 3 }}>
                Payment
              </Typography> */}

              {/* <h2
                style={{
                  marginTop: '0',
                  marginBottom: '25px',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  // borderBottom: '1px solid #ccc',
                }}
              >
                PAYMENT METHOD
              </h2> */}

              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  {/* <Stack spacing={2}>
                    {purchaseError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {formatQueryError(purchaseError)}
                      </Alert>
                    )} */}

                  <MainCard
                    key={values.isNewPaymentMethod ? 'active' : 'inactive'}
                    title="Payment"
                    // sx={{
                    //   border: values.isNewPaymentMethod
                    //     ? '2px solid #1976D2'
                    //     : '1px solid #ccc',
                    //   boxShadow: values.isNewPaymentMethod
                    //     ? '0 0 10px rgba(25, 118, 210, 0.5)'
                    //     : 'none',
                    //   borderRadius: 1,
                    //   transition: 'all 0.3s ease',
                    //   p: 2,
                    //   '&:hover': {
                    //     border: values.isNewPaymentMethod
                    //       ? '2px solid #1976D2'
                    //       : '1px solid #ccc',
                    //     boxShadow: values.isNewPaymentMethod
                    //       ? '0 0 10px rgba(25, 118, 210, 0.5)'
                    //       : 'none',
                    //   },
                    // }}

                    sx={{
                      // border: '1px solid #ccc',
                      borderRadius: 1,
                      // p: 2,
                      // '&:hover': {
                      //   border: '1px solid #ccc',
                      // },
                    }}
                  >
                    <Box
                    // sx={{
                    //   border: values.isNewPaymentMethod === true
                    //     ? '2px solid #1976D2'
                    //     : '1px solid #ccc',
                    //   boxShadow: values.isNewPaymentMethod === true
                    //     ? '0 0 10px rgba(25, 118, 210, 0.5)'
                    //     : 'none',
                    //   borderRadius: 1,
                    //   transition: 'all 0.3s ease',
                    //   p: 2,
                    // }}
                    >
                      {/* {category === 'Video Plugin' &&
                        paymentMethods &&
                        paymentMethods.length < 1 &&
                        stripePaymentMethods &&
                        stripePaymentMethods?.length > 0 && (
                          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                            For security reasons, we need you to re-enter your
                            credit card details before completing this purchase.
                          </Alert>
                        )}
                      {category === 'Subscription' &&
                        paymentMethods &&
                        paymentMethods.length < 1 &&
                        braintreePaymentMethods &&
                        braintreePaymentMethods?.length > 0 && (
                          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                            For security reasons, we need you to re-enter your
                            credit card details before completing this purchase.
                          </Alert>
                        )} */}
                      {(shouldShowStripeAlert || shouldShowBraintreeAlert) && (
                        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                          For security reasons, we need you to re-enter your
                          credit card details before completing this purchase.
                        </Alert>
                      )}
                      {purchaseError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {formatQueryError(purchaseError)}
                        </Alert>
                      )}
                      {isLoggedIn &&
                        paymentMethods &&
                        paymentMethods.length > 0 && (
                          <ExistingPaymentMethods
                            // category={productDetails?.category}
                            paymentMethods={paymentMethods}
                            category={
                              provider === 'stripe'
                                ? 'Subscription'
                                : 'Video Plugin'
                            }
                          />
                        )}
                      {isLoggedIn &&
                        paymentMethods &&
                        paymentMethods.length > 0 && (
                          <Divider
                            sx={{ borderColor: 'rgba(0, 0, 0, 0.3)', mb: 2 }}
                          />
                        )}

                      {isLoggedIn && (
                        <FormControlLabel
                          control={
                            <Radio
                              checked={values.isNewPaymentMethod}
                              onChange={(e) =>
                                setFieldValue(
                                  'isNewPaymentMethod',
                                  e.target.checked,
                                )
                              }
                              value="newCard"
                            />
                          }
                          label={
                            <Typography variant="subtitle1" fontWeight="bold">
                              New Credit Card
                            </Typography>
                          }
                          labelPlacement="end"
                        />
                      )}
                      <Box
                        sx={{
                          opacity: values.isNewPaymentMethod ? 1 : 0.4,
                          pointerEvents: values.isNewPaymentMethod
                            ? 'auto'
                            : 'none',
                          mb: 2,
                        }}
                      >
                        {provider === 'stripe' ? (
                          <StripeCreditCardForm />
                        ) : (
                          <PaymentMethodForm
                            disabled={!values.isNewPaymentMethod}
                            clientToken={braintreeClientToken ?? ''}
                            hostedFieldsInstance={hostedFieldsInstance}
                            setHostedFieldsInstance={setHostedFieldsInstance}
                          />
                        )}

                        <Typography variant="h6" sx={{ my: 2 }}>
                          Billing Address
                        </Typography>
                        <BillingAddressForm
                          disabled={!values.isNewPaymentMethod}
                        />

                        <AccountForm disabled={!values.isNewPaymentMethod} />

                        {/* <Grid container spacing={3} sx={{}}>
                          <Grid item xs={12} md={6}>
                            <BillingAddressForm
                              disabled={!values.isNewPaymentMethod}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <AccountForm
                              disabled={!values.isNewPaymentMethod}
                            />
                          </Grid>
                        </Grid> */}
                      </Box>

                      {/* <Box
                        sx={{
                          opacity: values.isNewPaymentMethod ? 1 : 0.4,
                          pointerEvents: values.isNewPaymentMethod
                            ? 'auto'
                            : 'none',
                          mb: 2,
                        }}
                      >
                        {provider === 'stripe' ? (
                          <StripeCreditCardForm />
                        ) : (
                          <PaymentMethodForm
                            disabled={!values.isNewPaymentMethod}
                            clientToken={braintreeClientToken ?? ''}
                            hostedFieldsInstance={hostedFieldsInstance}
                            setHostedFieldsInstance={setHostedFieldsInstance}
                          />
                        )}

                        <Typography variant="h6" sx={{ my: 2 }}>
                          Billing & Account Details
                        </Typography>

                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <BillingAddressForm
                              disabled={!values.isNewPaymentMethod}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <AccountForm
                              disabled={!values.isNewPaymentMethod}
                            />
                          </Grid>
                        </Grid>
                      </Box> */}

                      {/* <Divider /> */}
                      {/* <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.3)' }} /> */}
                      {/* <Divider sx={{ borderColor: 'grey.500' }} /> */}
                      {/* <Divider sx={{ borderColor: 'grey.700' }} /> */}
                      {/* <Divider sx={{ borderColor: '#999' }} /> */}

                      {/* <Divider sx={{ borderColor: 'grey.700', my: 1 }} /> */}

                      {/* {isLoggedIn && paymentMethods && (
                        <ExistingPaymentMethods
                          // category={productDetails?.category}
                          paymentMethods={paymentMethods}
                          category={
                            provider === 'stripe'
                              ? 'Subscription'
                              : 'Video Plugin'
                          }
                        />
                      )} */}
                    </Box>
                  </MainCard>

                  {/* {isLoggedIn && (
                      <ExistingPaymentMethods
                        // category={productDetails?.category}
                        category={
                          provider === 'stripe'
                            ? 'Subscription'
                            : 'Video Plugin'
                        }
                      />
                    )} */}
                  {/* </Stack> */}
                </Grid>

                <Grid item xs={12} md={4}>
                  {/* {isLoggedIn && (
                    <ExistingPaymentMethods
                      // category={productDetails?.category}
                      category={
                        provider === 'stripe' ? 'Subscription' : 'Video Plugin'
                      }
                      // selectedToken={values.existingPaymentMethodToken}
                      // onSelect={(token: string) =>
                      //   setFieldValue('existingPaymentMethodToken', token)
                      // }
                    />
                  )} */}

                  <MainCard
                    title="Order Summary"
                    sx={{ borderRadius: '4px 4px 0 0' }}
                    content={false}
                  >
                    <OrderSummary
                      // productDetails={productDetails}
                      cartData={cartData}
                      // selectedPlan={values.selectedPlan}
                      // onSelectPlan={(plan: string) =>
                      //   setFieldValue('selectedPlan', plan)
                      // }
                    />
                  </MainCard>

                  <Box
                    sx={
                      {
                        // display: 'flex',
                        // justifyContent: 'flex-end',
                        // gap: 2,
                        // mt: 3,
                      }
                    }
                  >
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting}
                      fullWidth
                      sx={{
                        my: 2,
                        mr: 3,
                        backgroundColor: '#1E88E5', // Custom blue
                        // textTransform: 'none',
                        fontWeight: 'bold',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: '#1565C0', // Darker blue on hover
                        },
                      }}
                    >
                      Purchase
                      {/* Place your order */}
                    </Button>

                    <Link to="/">
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{
                          fontWeight: 'bold',
                          // textTransform: 'none',
                          // mt: 2,
                        }}
                      >
                        Cancel
                      </Button>
                    </Link>
                  </Box>

                  <div
                    style={{
                      // backgroundColor: '#fff',
                      borderRadius: '8px',
                      padding: '20px',
                      // boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <p>
                      <b>Trouble Checking Out?</b>
                    </p>
                    <p>
                      You can also purchase at{' '}
                      <a
                        href="https://store.digitalanarchy.com"
                        target="_blank"
                        rel="noreferrer"
                        style={{ textDecoration: 'none' }}
                      >
                        store.digitalanarchy.com
                      </a>{' '}
                      or contact us.
                    </p>
                    <p>
                      📞 <b>415-287-6069</b>, 10am–5pm PST
                    </p>
                    <p>
                      <b>Digital Downloads</b>
                    </p>
                    <p>
                      All products/invoices are emailed to you. Check spam!
                      Don&apos;t see it? Email <b>sales@digitalanarchy.com</b>.
                    </p>
                  </div>
                </Grid>
                {/* <Grid item xs={12}>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={isSubmitting}
                    sx={{
                      my: 3,
                      mr: 3,
                      backgroundColor: '#1E88E5', // Custom blue
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: '#1565C0', // Darker blue on hover
                      },
                    }}
                  >
                    Purchase
                  </Button>

                  <Link to="/">
                    <Button variant="contained" color="error">
                      Cancel
                    </Button>
                  </Link>
                </Grid> */}
              </Grid>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}

export default Purchase;
